<template>
  <div>
    <general-table
      ref="generalTable"
      :api-url="APIURL"
      :delete-content="false"
      :block-content="false"
      :edit-content="false"
      :view-content="false"
      :columns="columns"
      :type="type"
    >
      <!-- Office Name column (supports multiple backend field names) -->
      <template #cell(office_name)="data">
        <span>
          {{ getOfficeName(data.item) }}
        </span>
      </template>

      <!-- List Committee column -->
      <template #cell(list_committee)="data">
        <b-btn
          variant="outline-dark"
          size="sm"
          class="my-1"
          @click="openViewCommitteeModal(data.item)"
        >
          <feather-icon
            icon="UsersIcon"
            class="cursor-pointer mr-50"
          />
          List Committee
        </b-btn>
      </template>

      <!-- Actions column -->
      <template #cell(actions_committee)="data">
        <div class="d-flex justify-content-center">
          <b-btn
            variant="outline-dark"
            size="sm"
            class="my-1"
            @click="openEditCommitteeModal(data.item)"
          >
            <feather-icon
              icon="EditIcon"
              class="cursor-pointer mr-50"
            />
            Edit Committee
          </b-btn>
        </div>
      </template>
    </general-table>

    <!-- View Committee Modal -->
    <DecisionCommitteeViewModal
      v-model="isViewModalVisible"
      :office-name="currentOfficeName"
      :loading="viewLoading"
      :items="committeeMembersView"
      :fields="viewFields"
    />

    <!-- Edit Committee Modal -->
    <DecisionCommitteeEditModal
      v-model="isEditModalVisible"
      :office-name="currentOfficeName"
      :edit-loading="editLoading"
      :save-loading="saveLoading"
      :members="committeeMembersEdit"
      :fields="editFields"
      :role-options="roleOptions"
      :new-member="newMember"
      @update:newMember="newMember = $event"
      @add-member="addMemberToList"
      @remove-member="removeMember"
      @toggle-active="toggleMemberActive"
      @save="saveCommittee"
    />
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import GeneralTable from '@/components/general-table/GeneralTable.vue'
import decryptData from '@/store/auth/decryptData'
import DecisionCommitteeViewModal from './DecisionCommitteeViewModal.vue'
import DecisionCommitteeEditModal from './DecisionCommitteeEditModal.vue'

export default {
  components: {
    GeneralTable,
    DecisionCommitteeViewModal,
    DecisionCommitteeEditModal,
  },
  data() {
    return {
      APIURL: '/halal/decision-committee/offices',
      type: 'page',
      columns: [
        { key: 'office_name', label: 'Office Name' },
        { key: 'list_committee', label: 'List Committee' },
        { key: 'actions_committee', label: 'Actions' },
      ],

      // view modal state
      isViewModalVisible: false,
      viewLoading: false,
      committeeMembersView: [],

      // edit modal state
      isEditModalVisible: false,
      saveLoading: false,
      committeeMembersEdit: [],
      editLoading: false,

      // shared office context
      currentOffice: null,

      // new member form
      newMember: {
        role: null,
        name: '',
        email: '',
        phone: '',
        active: true,
      },

      rolesLoading: false,
      roles: [],
    }
  },

  computed: {
    currentOfficeId() {
      if (!this.currentOffice) return null
      return this.currentOffice.office_id || this.currentOffice.id
    },
    currentOfficeName() {
      if (!this.currentOffice) return ''
      return this.getOfficeName(this.currentOffice)
    },
    roleOptions() {
      const rolesArray = Array.isArray(this.roles) ? this.roles : []
      return rolesArray.map(role => ({
        value: role.id,
        label: role.name,
      }))
    },
    viewFields() {
      return [
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'active', label: 'Active' },
        { key: 'role_name', label: 'Role' },
      ]
    },
    editFields() {
      return [
        { key: 'name', label: 'Name' },
        { key: 'phone', label: 'Phone' },
        { key: 'active', label: 'Active' },
        { key: 'role_name', label: 'Role' },
        { key: 'actions', label: 'Actions' },
      ]
    },
  },

  methods: {
    getOfficeName(office) {
      if (!office) return ''
      return office.office_name || office.name || office.office || office.office_country || ''
    },

    getRoleNameById(id) {
      const role = this.roles.find(r => r.id === id)
      return role ? role.name : null
    },

    normalizeCommitteeList(rawList) {
      if (!rawList) return []

      // Support multiple possible shapes:
      // - Array of members
      // - Object with data.members
      // - Object with members
      let list = []
      if (Array.isArray(rawList)) {
        list = rawList
      } else if (rawList && Array.isArray(rawList.members)) {
        list = rawList.members
      } else if (rawList && rawList.data && Array.isArray(rawList.data.members)) {
        list = rawList.data.members
      } else if (rawList && Array.isArray(rawList.data)) {
        list = rawList.data
      }

      return list.map(member => {
        const roles = Array.isArray(member.roles) ? member.roles : []
        const roleNames = roles.map(r => r.name).filter(Boolean)
        const primaryRole = roles[0] || {}
        let activeValue = 1
        if (typeof member.active !== 'undefined') {
          activeValue = member.active
        } else if (typeof member.is_active !== 'undefined') {
          activeValue = member.is_active
        }

        return {
          id: member.id,
          name: member.name,
          email: member.email,
          phone: member.phone,
          active: activeValue,
          // For editing we work with a single (primary) role
          role_id: primaryRole.id || null,
          // For viewing we show all role names as a comma-separated string
          role_name: roleNames.join(', '),
        }
      })
    },

    async openViewCommitteeModal(office) {
      this.currentOffice = office
      this.isViewModalVisible = true
      await this.fetchCommitteeForView()
    },

    async openEditCommitteeModal(office) {
      this.currentOffice = office
      this.isEditModalVisible = true
      this.newMember = {
        role: null,
        name: '',
        email: '',
        phone: '',
        active: true,
      }

      await Promise.all([
        this.fetchRolesIfNeeded(),
        this.fetchCommitteeForEdit(),
      ])
    },

    async fetchCommitteeForView() {
      if (!this.currentOfficeId) return

      this.viewLoading = true
      this.committeeMembersView = []

      try {
        const response = await axios.get(`/halal/decision-committee/offices/${this.currentOfficeId}/committee`)
        const decrypted = response.data && response.data.payload
          ? decryptData(response.data.payload)
          : response.data

        this.committeeMembersView = this.normalizeCommitteeList(decrypted)
      } catch (error) {
        this.committeeMembersView = []
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to load committee',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.viewLoading = false
      }
    },

    async fetchCommitteeForEdit() {
      if (!this.currentOfficeId) return

      this.committeeMembersEdit = []
      this.editLoading = true

      try {
        const response = await axios.get(`/halal/decision-committee/offices/${this.currentOfficeId}/committee`)
        const decrypted = response.data && response.data.payload
          ? decryptData(response.data.payload)
          : response.data
        this.committeeMembersEdit = this.normalizeCommitteeList(decrypted)
      } catch (error) {
        this.committeeMembersEdit = []
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to load committee',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.editLoading = false
      }
    },

    async fetchRolesIfNeeded() {
      if (this.roles.length || this.rolesLoading) return

      this.rolesLoading = true
      try {
        const response = await axios.get('/halal/decision-committee/roles')
        const decrypted = response.data && response.data.payload
          ? decryptData(response.data.payload)
          : response.data

        // Expected shape: { success: true, data: { roles: [...] }, message: '' }
        const list = decrypted && decrypted.data && Array.isArray(decrypted.data.roles)
          ? decrypted.data.roles
          : []
        this.roles = list
      } catch (error) {
        this.roles = []
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to load roles',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.rolesLoading = false
      }
    },

    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(String(email || '').toLowerCase())
    },

    addMemberToList() {
      const {
        role, name, email, phone, active,
      } = this.newMember
      if (!role || !name || !name.trim() || !email || !email.trim() || !phone || !phone.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'All fields are required',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      if (!this.validateEmail(email)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Invalid email format',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      const roleName = this.getRoleNameById(role)

      this.committeeMembersEdit.push({
        id: null,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        active,
        role_id: role,
        role_name: roleName,
      })

      this.newMember = {
        role: null,
        name: '',
        email: '',
        phone: '',
        active: true,
      }
    },

    removeMember(index) {
      this.committeeMembersEdit.splice(index, 1)
    },

    toggleMemberActive({ index, value }) {
      if (this.committeeMembersEdit[index]) {
        this.$set(this.committeeMembersEdit[index], 'active', !!value)
      }
    },

    async saveCommittee() {
      if (!this.currentOfficeId) return

      // Final validation before submit
      if (!this.committeeMembersEdit.length) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Committee list cannot be empty',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      const invalidMember = this.committeeMembersEdit.find(member => !member.name || !member.name.trim()
          || !member.role_id
          || !member.email
          || !this.validateEmail(member.email))

      if (invalidMember) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Please check member details (name, role, email)',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      const payload = this.committeeMembersEdit.map(member => ({
        name: member.name.trim(),
        email: member.email.trim(),
        phone: (member.phone || '').trim(),
        // API works with full replacement; backend expects roles array
        active: member.active ? 1 : 0,
        role_ids: member.role_id ? [member.role_id] : [],
      }))

      this.saveLoading = true

      try {
        // FULL REPLACEMENT of committee list
        await axios.put(
          `/halal/decision-committee/offices/${this.currentOfficeId}/committee`,
          { members: payload },
        )

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Committee updated successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        this.isEditModalVisible = false

        if (this.$refs.generalTable && this.$refs.generalTable.getAllData) {
          this.$refs.generalTable.getAllData()
        }

        if (this.isViewModalVisible) {
          await this.fetchCommitteeForView()
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to update committee',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.saveLoading = false
      }
    },
  },
}
</script>
